import { useEffect, useRef, useState } from "react";

export type MicPreviewStatus = "idle" | "ready" | "error";

/**
 * Opens a preview `getUserMedia` stream for the Audio Setup step's mic
 * picker (MDRS-268), BEFORE the real recording session starts — independent
 * of Transcriber's own capture pipeline (which only exists once a session is
 * live). Exposes just the live track (for TrackWaveform) and a status
 * ("Ready"/"Unavailable" badge); no AudioWorklet, no Azure connection.
 *
 * Tears itself down whenever `deviceId` changes or the step unmounts, so the
 * preview never overlaps with acquireMicAndAudioGraph's own getUserMedia
 * call once Start Recording is pressed — the two never run concurrently in
 * the UI flow this hook is used from.
 */
export const useMicPreview = (deviceId: string | undefined) => {
  const [track, setTrack] = useState<MediaStreamTrack | null>(null);
  const [status, setStatus] = useState<MicPreviewStatus>("idle");
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        setTrack(stream.getAudioTracks()[0] ?? null);
        setStatus("ready");
      } catch (err) {
        if (!cancelled) {
          console.warn("[useMicPreview] getUserMedia failed:", err);
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setTrack(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  return { track, status };
};
